/**
 * TenantSwitcher — iter-156bw.
 *
 * Renders for anyone holding the `impersonate_tenants` platform capability —
 * i.e. platform STAFF (limited, per-company grants) as well as support_admin
 * and super_admin. Hidden for every normal tenant user.
 *
 * UX:
 *   • Compact button in the top bar showing the currently active
 *     tenant name (or "My tenant" when not impersonating).
 *   • Click → dropdown listing every tenant the caller may access
 *     (returned by `GET /api/platform/tenants`).
 *   • Select a tenant → POST `/api/platform/switch-tenant`, then
 *     hard-reload the page so every cached query refetches against
 *     the new tenant.
 *   • A second action "Return to my tenant" is shown when
 *     impersonating — clears the cookie + reloads.
 *
 * Why hard-reload instead of a context refresh?
 *   React Query / SWR / our own apiCache all hold per-tenant
 *   responses keyed by URL alone. Switching tenants on the fly would
 *   leak stale data into the new context. A reload is a coarse but
 *   bulletproof bus-stop.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@/api/client';
import { setActiveTenant } from '@/lib/authToken';
import { Building2, ChevronDown, Check, LogOut, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import { toast } from 'sonner';

export const TenantSwitcher = () => {
  const { user, logout } = useAuth();
  const { canImpersonateTenants } = usePermissions();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [real, setReal] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [error, setError] = useState(null);  // why the list couldn't load (shown in the dropdown, not "no match")
  const [needsCode, setNeedsCode] = useState(false);
  const [missing, setMissing] = useState([]);  // assigned ids that match no company  // PLATFORM_MFA_REQUIRED: this session never passed a login code
  const wrapperRef = useRef(null);
  const seq = useRef(0);  // only the newest list request may land

  // FastAPI raises some errors with a dict `detail` ({code, message}); passing
  // that object straight to a toast/JSX renders "Objects are not valid as a
  // React child" and unmounts the switcher. Always coerce to a string.
  const errText = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.message || d.detail || 'Something went wrong';
    return e?.message || 'Something went wrong';
  };

  const fetchTenants = async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const { data } = await apiClient.get('/platform/tenants');
      if (mine !== seq.current) return;
      setError(null);
      setNeedsCode(false);
      setTenants(data?.tenants || []);
      setMissing(data?.assigned_missing || []);
      setActive(data?.active_tenant_id);
      setReal(data?.real_tenant_id);
      setImpersonating(!!data?.impersonating);
      // Keep the header copy in step with what the server actually applied.
      setActiveTenant(data?.impersonating ? data?.active_tenant_id : '');
    } catch (e) {
      if (mine !== seq.current) return;
      setError(errText(e));
      setNeedsCode(e?.response?.data?.detail?.code === 'PLATFORM_MFA_REQUIRED');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  };

  // Re-read on every open so a just-granted company shows up without a reload.
  const toggle = () => {
    if (!open) fetchTenants();
    setOpen(!open);
  };

  // Fetch on first open AND on user change (e.g. fresh login).
  // `fetchTenants` is recreated every render but only the email change
  // should re-trigger the fetch, hence the explicit dep + suppression
  // on the closure identity warning.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canImpersonateTenants) fetchTenants(); }, [user?.email, canImpersonateTenants]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.id || '').toLowerCase().includes(q),
    );
  }, [tenants, search]);

  const handleSwitch = async (tid) => {
    if (tid === active) { setOpen(false); return; }
    try {
      await apiClient.post('/platform/switch-tenant', { tenant_id: tid });
      setActiveTenant(tid);
      // Hard reload so every cached query refetches under the new tenant.
      window.location.reload();
    } catch (e) {
      toast.error('Tenant switch failed', { description: errText(e) });
    }
  };

  const handleClear = async () => {
    try {
      await apiClient.post('/platform/clear-tenant-switch', {});
      setActiveTenant('');
      window.location.reload();
    } catch (e) {
      toast.error('Failed to exit tenant', { description: errText(e) });
    }
  };

  if (!canImpersonateTenants) return null;

  const activeTenant = tenants.find((t) => t.id === active);

  return (
    <div className="relative" ref={wrapperRef} data-testid="tenant-switcher">
      <button
        onClick={toggle}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium border border-border hover:bg-accent/40 transition-colors"
        style={{ color: impersonating ? '#C4A561' : 'var(--strata-text-primary)' }}
        data-testid="tenant-switcher-button"
      >
        <Building2 className="w-3.5 h-3.5" />
        <span className="max-w-[180px] truncate">
          {activeTenant?.name || (impersonating ? 'Viewing tenant…' : 'My tenant')}
        </span>
        {impersonating && (
          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold bg-[#C4A561]/15 text-[#C4A561]">
            viewing
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5 opacity-50" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid="tenant-switcher-dropdown"
        >
          <div className="p-3 border-b border-border flex items-center gap-2 bg-muted/30">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants…"
              className="bg-transparent outline-none text-xs flex-1 text-foreground placeholder:text-muted-foreground"
              data-testid="tenant-switcher-search"
            />
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && !error && tenants.length === 0 ? (  // a re-read keeps the last list on screen
              <div className="p-4 text-xs text-muted-foreground" data-testid="tenant-switcher-loading">Loading…</div>
            ) : error ? (
              <div className="p-4 text-xs space-y-2" data-testid="tenant-switcher-error">
                <div className="font-medium text-foreground">Couldn't load your companies</div>
                <div className="text-muted-foreground" data-testid="tenant-switcher-error-text">{error}</div>
                {needsCode ? (
                  <button onClick={async () => { try { await logout(); } finally { window.location.href = '/login'; } }} className="text-[#4575A8] font-medium hover:underline" data-testid="tenant-switcher-signout">
                    Sign out to sign back in
                  </button>
                ) : (
                  <button onClick={fetchTenants} className="text-[#4575A8] font-medium hover:underline" data-testid="tenant-switcher-retry">Try again</button>
                )}
              </div>
            ) : tenants.length === 0 && missing.length > 0 ? (
              <div className="p-4 text-xs text-muted-foreground" data-testid="tenant-switcher-missing">
                {missing.length === 1 ? 'The company' : `${missing.length} companies`} assigned to you can't be found (deleted, or the assignment is out of date). A super admin can re-add it on Platform → Roles.
              </div>
            ) : tenants.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground" data-testid="tenant-switcher-empty">
                No companies are assigned to you yet. A super admin can add one on Platform → Roles.
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground" data-testid="tenant-switcher-no-match">No tenants match "{search}"</div>
            ) : (
              filtered.map((t) => {
                const isActive = t.id === active;
                const isHome = t.id === real;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleSwitch(t.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-accent/40 transition-colors ${isActive ? 'bg-accent/30' : ''}`}
                    data-testid={`tenant-option-${t.id}`}
                  >
                    <span className="flex-1 min-w-0 truncate text-foreground">{t.name || t.id}</span>
                    {isHome && (
                      <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#4575A8]/15 text-[#4575A8]">home</span>
                    )}
                    {isActive && <Check className="w-3.5 h-3.5 text-[#4575A8]" />}
                  </button>
                );
              })
            )}
          </div>
          {impersonating && (
            <button
              onClick={handleClear}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-xs border-t border-border bg-muted/30 hover:bg-muted/60 text-[#C4A561] font-medium"
              data-testid="tenant-switcher-exit"
            >
              <LogOut className="w-3.5 h-3.5" />
              Return to my tenant
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
